import React, { useState, useEffect } from 'react';
import {
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  EyeIcon,
  CheckCircleIcon,
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { API_BASE } from '../../lib/config';

const AbuseReports = ({ onBack }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [stats, setStats] = useState({ pending: 0, in_progress: 0, resolved: 0, total: 0 });

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [statusFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      // Fetch pending reports for "Quick Action"
      const response = await fetch(`${API_BASE}/admin/reports?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data);
      } else {
        setReports([]);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('通報情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/reports/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Error fetching report stats:', e);
    }
  };

  const handleReportAction = async (reportId, action, notes = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      const method = action === 'resolve' ? 'resolved' : 'rejected'; // map action to backend status

      const response = await fetch(`${API_BASE}/admin/reports/${reportId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: method,
          admin_note: notes
        })
      });

      if (!response.ok) throw new Error('API Error');

      toast.success(`通報を${action === 'resolve' ? '解決' : '却下'}しました`);
      fetchReports(); // データを再取得
      fetchStats(); // 統計も更新
    } catch (error) {
      toast.error('操作に失敗しました');
    }
  };

  const getReportTypeBadge = (type) => {
    const types = {
      harassment: { class: 'bg-red-100 text-red-800', label: 'ハラスメント' },
      no_show: { class: 'bg-yellow-100 text-yellow-800', label: '無断欠勤' },
      fake_job: { class: 'bg-purple-100 text-purple-800', label: '虚偽求人' },
      inappropriate: { class: 'bg-orange-100 text-orange-800', label: '不適切行為' }
    };
    const badge = types[type] || { class: 'bg-gray-100 text-gray-800', label: 'その他' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const getSeverityBadge = (severity) => {
    const severities = {
      high: { class: 'bg-red-100 text-red-800', label: '高' },
      medium: { class: 'bg-yellow-100 text-yellow-800', label: '中' },
      low: { class: 'bg-green-100 text-green-800', label: '低' }
    };
    const badge = severities[severity] || severities.medium;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            ダッシュボードに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-600 mr-3" />
            通報管理
          </h2>
          <p className="text-gray-600 mt-1">ユーザーからの通報を確認・対応</p>
        </div>

        {/* ステータスフィルター */}
        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-gray-700">ステータス:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="pending">対応待ち</option>
            <option value="in_progress">対応中</option>
            <option value="resolved">解決済み</option>
          </select>
        </div>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">対応待ち</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}件</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">対応中</p>
              <p className="text-2xl font-bold text-gray-900">{stats.in_progress}件</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">解決済み</p>
              <p className="text-2xl font-bold text-gray-900">{stats.resolved}件</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UserIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">総通報数</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}件</p>
            </div>
          </div>
        </div>
      </div>

      {/* 通報一覧 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">通報一覧</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500">読み込み中...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    通報内容
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    通報者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    被通報者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    種類・重要度
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    報告日時
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{report.title}</div>
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {report.description}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{report.reporter_name}</div>
                      <div className="text-xs text-gray-500">{report.reporter_id}</div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{report.reported_user_name}</div>
                      <div className="text-xs text-gray-500">{report.reported_user_id}</div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {getReportTypeBadge(report.report_type)}
                        {getSeverityBadge(report.severity)}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(report.created_at)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => alert(`通報ID: ${report.id}の詳細を表示`)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          詳細
                        </button>
                        <button
                          onClick={() => handleReportAction(report.id, 'resolve')}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          解決
                        </button>
                        <button
                          onClick={() => alert(`通報者: ${report.reporter_name}にメッセージを送信`)}
                          className="text-purple-600 hover:text-purple-900 flex items-center"
                        >
                          <ChatBubbleLeftIcon className="w-4 h-4 mr-1" />
                          返信
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {reports.length === 0 && !loading && (
          <div className="p-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">該当する通報がありません</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AbuseReports;